import React, { useState, useMemo } from 'react';
import { bool, string, func } from 'prop-types';
import { Flex } from 'rebass';
import join from 'url-join';
import { TextFieldStyled, TextStyled } from '@nike/nike-design-system-components';
import Modal from '../../../modal/Modal';
import { Button } from '../../../components';
import { trimNSplit } from '../../../util/trim';
import { getAccountNumberColumns } from '../accountNumberColumns';
import TableClientSide from '../../../components/tableClientSide/TableClientSide';
import { http } from '../../../util/index';

const api2 = import.meta.env.VITE_API2_URL;

const AddAccountsModal = ({ isOpen, closeModal, title, actionName }) => {
  const [inputValue, setInputValue] = useState('');
  const [accountResponse, setAccountResponse] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const memoizedColumns = useMemo(() => getAccountNumberColumns(), []);

  const onAddAccountChange = e => {
    setInputValue(e.target.value);
  };

  const clearInput = () => {
    setInputValue('');
    setAccountResponse([]);
    setErrorMessage('');
  };

  const handleCloseModal = () => {
    clearInput();
    closeModal();
  };

  const addAccountNumbersAction = async accountNumbers => {
    const url = join(api2, 'v1/admin/importAccounts');
    const payload = { accountNumbers };
    try {
      const response = await http.post(url, payload);
      const result = [];
      if (response?.data) {
        accountNumbers.forEach(element => {
          result.push(response.data.find(data => data.accountNumber.toString() === element));
        });
      }
      setErrorMessage('');
      return result;
    } catch (error) {
      setErrorMessage('Something went wrong. No Accounts were added');
      return [];
    }
  };

  const content = ({ isLoading }) => {
    const isTextFieldDisabled = isLoading;
    return (
      <Flex flexDirection='column' justifyContent='center'>
        {accountResponse.length === 0 && (
          <TextFieldStyled
            id='accountNumber'
            name='accountNumber'
            label='Enter Account Numbers'
            supportText='Paste a list of account numbers here (separated by spaces).'
            autoComplete='off'
            value={inputValue}
            disabled={isTextFieldDisabled}
            onChange={onAddAccountChange}
          />
        )}
        {accountResponse.length > 0 && (
          <TableClientSide
            columns={memoizedColumns}
            data={accountResponse}
            isLoading={isLoading}
            height='300'
            noBorders
          />
        )}
        {errorMessage && (
          <Flex p='12px' width='100%' flexDirection='row'>
            <TextStyled appearance='body2' color='error'>
              {errorMessage}
            </TextStyled>
          </Flex>
        )}
      </Flex>
    );
  };

  const footer = ({ isLoading, toggleIsLoading }) => {
    const isSubmitButtonDisabled = isLoading || inputValue === '';
    const handleSubmit = async () => {
      const dedupedAccountNumbers = new Set(trimNSplit(inputValue));
      const accountNumbers = [...dedupedAccountNumbers];
      toggleIsLoading(true, 'Adding Accounts...');
      if (accountNumbers.length) {
        const response = await addAccountNumbersAction(accountNumbers);
        setAccountResponse(response);
      }
      toggleIsLoading(false);
    };
    return (
      <Flex justifyContent='center'>
        {accountResponse.length === 0 && (
          <Button
            data-testid='add_account_numbers'
            extraClassName='ncss-btn-primary-dark'
            disabled={isSubmitButtonDisabled}
            onClick={handleSubmit}
          >
            {actionName}
          </Button>
        )}
      </Flex>
    );
  };

  return (
    <Modal
      title={accountResponse.length === 0 ? title : 'Account Status'}
      isOpen={isOpen}
      closeModal={handleCloseModal}
      content={content}
      footer={footer}
      modalRootCompProps={{ style: { maxWidth: '700px' } }}
      unwrapped
    />
  );
};

AddAccountsModal.propTypes = {
  title: string.isRequired,
  isOpen: bool.isRequired,
  closeModal: func.isRequired,
  actionName: string.isRequired
};

export default AddAccountsModal;
